/**
 * ConfidenceHeatmap — Admin view aggregating confidence-survey submissions
 * per lesson so curriculum owners can see which units consistently produce
 * low self-reported confidence.
 *
 * Data source: GET /api/admin/confidence/heatmap?days=N
 *
 * Row shape:
 *   { lesson_id, lesson_title, course_title, count, avg_confidence,
 *     low_count, mid_count, high_count, pct_struggling, help_count,
 *     last_at, sample_note }
 *
 * Sorted by pct_struggling desc — the roughest lessons rise to the top.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Flame, HelpCircle, RefreshCw, ExternalLink, TrendingUp, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import StrugglingStudentsDrawer from './StrugglingStudentsDrawer';

const WINDOW_OPTIONS = [
  { value: 7,  label: 'Last 7d'  },
  { value: 30, label: 'Last 30d' },
  { value: 90, label: 'Last 90d' },
];

// Colour band for the pct_struggling badge. Higher = redder.
const bandFor = (pct) => {
  if (pct >= 50) return 'bg-rose-500/15 text-rose-700 border-rose-500/40';
  if (pct >= 25) return 'bg-amber-500/15 text-amber-700 border-amber-500/40';
  if (pct >= 10) return 'bg-yellow-500/15 text-yellow-700 border-yellow-500/40';
  return 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40';
};

// Mini stacked bar showing the low/mid/high distribution for one row.
const DistBar = ({ low, mid, high, total }) => {
  const safe = total || 1;
  const l = (low  / safe) * 100;
  const m = (mid  / safe) * 100;
  const h = (high / safe) * 100;
  return (
    <div
      className="w-full h-2 rounded-full overflow-hidden bg-muted flex"
      title={`Struggling ${low} · Neutral ${mid} · Confident ${high}`}
    >
      <div style={{ width: `${l}%` }} className="bg-rose-500" />
      <div style={{ width: `${m}%` }} className="bg-amber-400" />
      <div style={{ width: `${h}%` }} className="bg-emerald-500" />
    </div>
  );
};

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const ConfidenceHeatmap = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  // Drilldown drawer state — click a row to see per-user detail + DM CTA.
  const [drill, setDrill] = useState({ open: false, lessonId: null, lessonTitle: '' });

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/admin/confidence/heatmap?days=${d}`);
      setData(resp);
    } catch (_) {
      toast.error('Could not load confidence heatmap');
      setData({ summary: { lessons_covered: 0, total_ratings: 0, low_ratings: 0, pct_struggling: 0, avg_confidence: 0, help_requests: 0 }, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <section className="pro-card p-4 mb-5" data-testid="confidence-heatmap">
      <div className="flex items-start gap-2 flex-wrap mb-3">
        <div className="h-8 w-8 rounded-full bg-rose-500/15 text-rose-600 flex items-center justify-center flex-shrink-0">
          <Flame className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="eyebrow text-rose-600">Confidence Heatmap</div>
          <div className="text-xs text-muted-foreground max-w-lg">
            Lessons ranked by the share of students who rated themselves &le;&nbsp;2/5.
            High percentages flag units that need better explanation, more practice, or a
            content refresh.
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold" role="tablist" data-testid="heatmap-window-tabs">
            {WINDOW_OPTIONS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => setDays(w.value)}
                data-testid={`heatmap-window-${w.value}`}
                className={`px-3 h-8 transition-colors inline-flex items-center gap-1 ${
                  days === w.value ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(days)}
            className="rounded-full"
            data-testid="heatmap-refresh"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Rollup */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="heatmap-summary">
        <SummaryTile label="Lessons rated"   value={summary.lessons_covered ?? 0} />
        <SummaryTile label="Total ratings"   value={summary.total_ratings ?? 0} />
        <SummaryTile label="Avg confidence"  value={`${summary.avg_confidence ?? 0}/5`} />
        <SummaryTile
          label="Struggling"
          value={`${summary.pct_struggling ?? 0}%`}
          tone={(summary.pct_struggling ?? 0) >= 25 ? 'danger' : 'ok'}
        />
        <SummaryTile label="Help requests"   value={summary.help_requests ?? 0} icon={<HelpCircle className="w-3 h-3" />} />
      </div>

      {/* Rows */}
      {rows.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="heatmap-empty">
          <TrendingUp className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No confidence ratings in this window yet. Encourage students to complete a lesson &mdash; each one prompts a quick self-rating.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border" data-testid="heatmap-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Lesson</th>
                <th className="px-3 py-2 text-left">Course</th>
                <th className="px-3 py-2 text-right">Ratings</th>
                <th className="px-3 py-2 text-right">Avg</th>
                <th className="px-3 py-2 text-right">Struggling</th>
                <th className="px-3 py-2 text-left w-40">Distribution</th>
                <th className="px-3 py-2 text-left">Last</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr
                  key={r.lesson_id}
                  data-testid={`heatmap-row-${r.lesson_id}`}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => setDrill({ open: true, lessonId: r.lesson_id, lessonTitle: r.lesson_title })}
                >
                  <td className="px-3 py-2">
                    <div className="font-semibold text-foreground line-clamp-1">{r.lesson_title}</div>
                    {r.sample_note && (
                      <div className="text-[11px] text-muted-foreground italic line-clamp-1">&ldquo;{r.sample_note}&rdquo;</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.course_title}</td>
                  <td className="px-3 py-2 text-right text-xs font-bold text-foreground">{r.count}</td>
                  <td className="px-3 py-2 text-right text-xs font-bold text-foreground">{r.avg_confidence}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex items-center rounded-full border font-bold text-[10px] px-2 py-0.5 ${bandFor(r.pct_struggling)}`}>
                      {r.pct_struggling}%
                    </span>
                    {r.help_count > 0 && (
                      <span
                        className="ml-1 inline-flex items-center rounded-full border font-bold text-[10px] px-2 py-0.5 bg-primary/10 text-primary border-primary/30"
                        title={`${r.help_count} students clicked "I need help"`}
                      >
                        <HelpCircle className="w-2.5 h-2.5 mr-0.5" /> {r.help_count}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <DistBar low={r.low_count} mid={r.mid_count} high={r.high_count} total={r.count} />
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {r.low_count} · {r.mid_count} · {r.high_count}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap">{rel(r.last_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDrill({ open: true, lessonId: r.lesson_id, lessonTitle: r.lesson_title })}
                      className="rounded-full mr-1"
                      data-testid={`heatmap-students-${r.lesson_id}`}
                      title="See struggling students"
                    >
                      <Users className="w-3 h-3 mr-1" /> Students
                    </Button>
                    {r.course_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/lms?course=${r.course_id}`)}
                        className="rounded-full"
                        data-testid={`heatmap-edit-${r.lesson_id}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Struggling students drilldown drawer */}
      <StrugglingStudentsDrawer
        open={drill.open}
        lessonId={drill.lessonId}
        lessonTitle={drill.lessonTitle}
        days={days}
        onClose={() => setDrill({ open: false, lessonId: null, lessonTitle: '' })}
      />
    </section>
  );
};

const SummaryTile = ({ label, value, tone, icon }) => (
  <div className={`rounded-xl border p-3 ${tone === 'danger' ? 'border-rose-300 bg-rose-50/60 dark:bg-rose-900/10' : 'border-border bg-muted/40'}`}>
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">
      {icon} {label}
    </div>
    <div className={`text-lg font-extrabold leading-tight ${tone === 'danger' ? 'text-rose-700' : 'text-foreground'}`}>{value}</div>
  </div>
);

export default ConfidenceHeatmap;
